import { hasCookie, setCookie } from "cookies-next";
import { useRouter } from "next/router";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { DiscountDetails } from "@/api/discount/types";
import { NotificationSuccess } from "@/components/NotificationSuccess";
import {
  ACCEPT_COOKIES_COOKIE_NAME,
  NEWSLETTER_BANNER_SEEN,
} from "@/constants";
import useBreakpointTest from "@/hooks/useBreakpointTest";
import useNotification from "@/hooks/useNotification";

type GlobalStateProps = {
  previousPath: string;
  hasAcceptedCookies: boolean;
  isClientSide: boolean;
  cartIsOpen: boolean;
  disableCartAutoClose: boolean;
  acceptCookies: () => void;
  toggleCartIsOpen: (forceState?: boolean) => void;
  setDisableCartAutoClose: Dispatch<SetStateAction<boolean>>;
  discountDetails: DiscountDetails;
  setDiscountDetails: Dispatch<SetStateAction<DiscountDetails>>;
  setRedirectPath: Dispatch<SetStateAction<string>>;
  setPreviousPath: Dispatch<SetStateAction<string>>;
  redirectPath: string;
  hasSeenNewsletterBanner: boolean;
  seeNewsletterBanner: () => void;
  setRegisterSuccess: Dispatch<SetStateAction<boolean>>;
};

const initialState: Partial<GlobalStateProps> = {
  previousPath: "",
  isClientSide: false,
  cartIsOpen: false,
  disableCartAutoClose: false,
  hasAcceptedCookies: hasCookie(ACCEPT_COOKIES_COOKIE_NAME),
  hasSeenNewsletterBanner: hasCookie(NEWSLETTER_BANNER_SEEN),
  seeNewsletterBanner: () => {},
  acceptCookies: () => {},
  discountDetails: null,
};

const GlobalState = createContext<GlobalStateProps>(
  initialState as GlobalStateProps
);

// Export the provider as we need to wrap the entire app with it
export const GlobalStateProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const router = useRouter();
  const [hasAcceptedCookies, setHasAcceptedCookies] = useState(
    hasCookie(ACCEPT_COOKIES_COOKIE_NAME)
  );
  const [hasSeenNewsletterBanner, setHasSeenNewsletterBanner] = useState(
    hasCookie(NEWSLETTER_BANNER_SEEN)
  );

  const [isClientSide, setIsClientSide] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [previousPath, setPreviousPath] = useState<string>("");
  const [redirectPath, setRedirectPath] = useState<string>("");
  const [cartIsOpen, setCartIsOpen] = useState<boolean>(
    initialState.cartIsOpen
  );
  const [discountDetails, setDiscountDetails] = useState<DiscountDetails>(null);
  const [disableCartAutoClose, setDisableCartAutoClose] = useState<boolean>(
    initialState.disableCartAutoClose
  );
  const [registerSuccess, setRegisterSuccess] = useState<boolean>(false);

  const { showNotification } = useNotification();
  const isDesktop = useBreakpointTest("md");

  function toggleCartIsOpen(forceState?: boolean) {
    setCartIsOpen((prevState) => forceState ?? !prevState);
  }

  function acceptCookies() {
    setCookie(ACCEPT_COOKIES_COOKIE_NAME, "yes");
    setHasAcceptedCookies(true);
  }

  function seeNewsletterBanner() {
    setCookie(NEWSLETTER_BANNER_SEEN, "yes");
    setHasSeenNewsletterBanner(true);
  }

  useEffect(() => {
    if (registerSuccess) {
      showNotification(
        <NotificationSuccess copy="create_account_confirmation" ns="common" />,
        isDesktop ? "top" : "bottom"
      );
    }

    return () => setRegisterSuccess(false);
  }, [router.pathname]);

  useEffect(() => setIsClientSide(typeof window !== "undefined"), []);

  useEffect(() => {
    const handleRouteChange = () => {
      if (router.pathname === redirectPath) {
        setRedirectPath("");
      }

      setPreviousPath(router.pathname);
    };

    router.events.on("beforeHistoryChange", handleRouteChange);

    return () => {
      router.events.off("beforeHistoryChange", handleRouteChange);
    };
  }, [router.pathname]);

  const memoedValue = useMemo(
    () => ({
      acceptCookies,
      cartIsOpen,
      disableCartAutoClose,
      hasAcceptedCookies,
      hasSeenNewsletterBanner,
      seeNewsletterBanner,
      isClientSide,
      previousPath,
      setDisableCartAutoClose,
      toggleCartIsOpen,
      discountDetails,
      setDiscountDetails,
      redirectPath,
      setRedirectPath,
      setRegisterSuccess,
      setPreviousPath,
    }),
    [
      previousPath,
      hasAcceptedCookies,
      hasSeenNewsletterBanner,
      cartIsOpen,
      disableCartAutoClose,
      isClientSide,
      discountDetails,
      registerSuccess,
      setPreviousPath,
    ]
  );

  return (
    <GlobalState.Provider value={memoedValue}>{children}</GlobalState.Provider>
  );
};

export default function useGlobalState() {
  return useContext(GlobalState);
}
